import { type IGetApiV5StatWordsApiResponse as searchCatalogStatWords } from '#adapter/api/campaigns/v5/stat-words/types';
import { type ICode200 as autoStatWords } from '#adapter/api/campaigns/v2/auto/{advertID}/stat-words/get';
import { type IGetApiV5WordsApiResponse as excludedAndPlusWords } from '#adapter/api/campaigns/v5/words/types';

import type { ICustomWord, IWord, ITab, TWordsTypes } from './types';
import { ETabType } from './const';

export function mapStringToIWord(keyword: string | ICustomWord): IWord {
    if(typeof keyword === 'object' && keyword !== null) {
        return {
            keyword : keyword.word ?? '',
            active  : false,
            selected: false,
            custom  : keyword.custom
        };
    }

    return {
        keyword,
        active  : false,
        selected: false
    };
}

export function mapIWordToString({ keyword }: IWord): string {
    return keyword;
}

export function mapIWordToFull(word: IWord): IWord {
    return {
        ...word,
        active  : false,
        selected: false
    };
}

export function mapIWordToSelected(word: IWord): IWord {
    return {
        ...word,
        selected: true
    };
}

export function setActiveTab(tabs: Array<ITab>, newActiveTab: ITab): Array<ITab> {
    return tabs.map(({ tab }) => (tab === newActiveTab.tab ? ({
        tab,
        active: true
    }) : ({
        tab,
        active: false
    })));
}

export const mapExcludedAndPlusToIState = ({ words }: excludedAndPlusWords) => {
    return {
        fixed   : Boolean(words?.fixed),
        excluded: words?.excluded?.map(mapStringToIWord) ?? [],
        pluse   : words?.plus?.map(mapStringToIWord) ?? []
    };
};

export const mapWordsToIState = ({ words }: searchCatalogStatWords) => {
    return words?.reduce((acc, cur) => {
        if(!cur?.keyword) {
            return acc;
        }

        return [...acc, mapIWordToFull({ ...cur, keyword: cur.keyword ?? '' })];
    }, [] as Array<IWord>) ?? [];
};

export function mapIResponseToIState({ words }: autoStatWords, isPosition = false): TWordsTypes & { fixed: boolean } {
    return {
        fixed   : Boolean(words?.fixed),
        excluded: words?.excluded?.map(mapStringToIWord) ?? [],
        keywords: words?.keywords?.reduce((acc, cur) => {
            if(!cur?.keyword) {
                return acc;
            }

            return [...acc, mapIWordToFull({
                ...cur,
                keyword : cur.keyword ?? '',
                position: isPosition ? ETabType.KEYWORDS : undefined
            })];
        }, [] as Array<IWord>) ?? [],
        phrase: words?.phrase?.map(mapStringToIWord) ?? [],
        pluse : words?.pluse?.map(mapStringToIWord) ?? [],
        strong: words?.strong?.map(mapStringToIWord) ?? []
    };
}

export const getUpdatedKeywords = (keywords: Array<IWord>, currKeywords: Array<IWord>) => {
    const allNewKeywords = new Set(keywords.map((item) => item.keyword));
    const allOldKeywords = currKeywords.reduce((acc, item) => ({
        ...acc,
        [item.keyword]: item
    }), {} as Record<string, IWord>);

    const newKeywords = keywords.map((item) => (
        allOldKeywords[item.keyword] !== undefined ? { ...item, count: allOldKeywords[item.keyword].count } : item)
    );
    const oldKeywords = currKeywords.filter((item) => !allNewKeywords.has(item.keyword));

    const allWords = [...newKeywords, ...oldKeywords];
    const firstNoCustomWordIndex = allWords.findIndex((item) => !item.custom);
    const sortedCustomKeywords = allWords.slice(0, firstNoCustomWordIndex)
        .sort((current, next) => (next?.count || 0) - (current?.count || 0));

    return [...sortedCustomKeywords, ...allWords.slice(firstNoCustomWordIndex, allWords.length)];
};

export const getSortWords = (words: Array<IWord>) => {
    const sortWords = [...words].sort((firstWord, secondWord) => {
        if(firstWord.custom && secondWord.custom) { return 0; }

        if(firstWord.custom && !secondWord.custom) { return -1; }

        return 1;
    });

    return sortWords;
};
